/**
 * RestoreWizardDialog — Feb 2026 · Iter94
 *
 * When a user hits Restore on an archived project, this dialog asks
 * for a fresh note explaining the reactivation so the audit trail
 * captures the *why* alongside the *who*. Returning the note (or
 * empty string) — the caller then PATCHes the project with
 * `archived: false` + `archive_note: <note>`.
 */
import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function RestoreWizardDialog({ open, onOpenChange, project, onConfirm }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (open) setNote(""); }, [open]);
  const submit = () => {
    onOpenChange(false);
    onConfirm?.(note.trim() || null);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="restore-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-widest uppercase text-sm">
            Restore project
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            You&apos;re about to bring{" "}
            <span className="font-semibold text-slate-800">
              {project?.name || "this project"}
            </span>{" "}
            back to the active list.
            {project?.archive_reason && (
              <> It was archived as <span className="uppercase font-mono">{project.archive_reason}</span>
                {project?.archived_by ? <> by {project.archived_by}</> : null}.</>
            )}
            {" "}Add a short note explaining the reactivation so the audit trail
            captures why the decision changed.
          </DialogDescription>
        </DialogHeader>

        <div className="pt-1">
          <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-mono mb-1">
            Reactivation note (optional)
          </label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Client reopened tender · reviewing revised commercial terms"
            data-testid="restore-wizard-note"
            className="min-h-[70px] text-[12.5px]"
          />
        </div>

        <DialogFooter>
          <Button variant="outline"
                     onClick={() => onOpenChange(false)}
                     data-testid="restore-wizard-cancel">
            Cancel
          </Button>
          <Button onClick={submit}
                    data-testid="restore-wizard-confirm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
            Restore project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
